// CTW-DURMAH-STUDENT-MANUAL-01:
// Page-level manual search. Instant, client-side, keyboard-navigable.
//
// Follows the house debounce + keyboard pattern from
// src/components/study/LexiconSearchOverlay.tsx, but as an inline page
// section rather than a modal, and over the local manual index rather than
// an API — so results appear as the student types with no network round-trip.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useRouter } from "next/router";
import Link from "next/link";
import { ArrowRight, Lock, Search, X } from "lucide-react";
import { searchManual, type ManualSearchHit } from "@/lib/manual/search";
import { getManualCategory } from "@/content/manual";

interface ManualSearchProps {
  /** Signed-in students see member sections without a lock badge. */
  isSignedIn: boolean;
  /** Rendered above the input. */
  placeholder?: string;
  autoFocus?: boolean;
}

const MAX_VISIBLE = 8;

export default function ManualSearch({
  isSignedIn,
  placeholder = "Search the manual — try “upload a lecture”, “voice”, “what grade will I get”",
  autoFocus = false,
}: ManualSearchProps) {
  const router = useRouter();
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isFocused, setIsFocused] = useState(false);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const resultRefs = useRef<Array<HTMLAnchorElement | null>>([]);

  // Debounce so we are not re-scoring the corpus on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query), 160);
    return () => clearTimeout(timer);
  }, [query]);

  const results: ManualSearchHit[] = useMemo(() => {
    if (!debounced.trim()) return [];
    return searchManual(debounced, { limit: MAX_VISIBLE });
  }, [debounced]);

  useEffect(() => {
    setActiveIndex(results.length > 0 ? 0 : -1);
    resultRefs.current = resultRefs.current.slice(0, results.length);
  }, [results]);

  useEffect(() => {
    if (activeIndex < 0) return;
    resultRefs.current[activeIndex]?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const clear = useCallback(() => {
    setQuery("");
    setDebounced("");
    setActiveIndex(-1);
    inputRef.current?.focus();
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.nativeEvent.isComposing) return;

      if (e.key === "Escape") {
        e.preventDefault();
        clear();
        return;
      }

      if (!results.length) return;

      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((prev) => {
          const current = prev >= 0 ? prev : 0;
          return e.key === "ArrowDown"
            ? Math.min(results.length - 1, current + 1)
            : Math.max(0, current - 1);
        });
        return;
      }

      if (e.key === "Enter" && activeIndex >= 0) {
        e.preventDefault();
        const hit = results[activeIndex];
        if (hit) void router.push(`/manual/${hit.section.slug}`);
      }
    },
    [activeIndex, clear, results, router],
  );

  const showResults = isFocused && debounced.trim().length > 0;

  return (
    <div className="relative" onKeyDownCapture={handleKeyDown}>
      <div className="relative">
        <Search
          className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none"
          aria-hidden="true"
        />
        <input
          ref={inputRef}
          type="search"
          role="searchbox"
          aria-label="Search the Student Manual"
          aria-autocomplete="list"
          aria-controls="manual-search-results"
          autoFocus={autoFocus}
          value={query}
          placeholder={placeholder}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsFocused(true)}
          // Delay so a click on a result is not cancelled by the blur.
          onBlur={() => window.setTimeout(() => setIsFocused(false), 150)}
          className="w-full pl-12 pr-12 py-4 rounded-2xl border border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900/50 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none text-gray-700 dark:text-slate-200 shadow-sm text-[15px]"
        />
        {query ? (
          <button
            type="button"
            onClick={clear}
            aria-label="Clear search"
            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-slate-200 transition-colors"
          >
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        ) : null}
      </div>

      {showResults ? (
        <div
          id="manual-search-results"
          role="listbox"
          aria-label="Search results"
          className="absolute z-30 mt-2 w-full rounded-2xl border border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-950 shadow-xl overflow-hidden max-h-[28rem] overflow-y-auto"
        >
          {results.length === 0 ? (
            <div className="px-5 py-8 text-center">
              <p className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1">
                Nothing in the manual matches “{debounced.trim()}”.
              </p>
              <p className="text-xs text-gray-500 dark:text-slate-500 mb-4">
                Try a plainer phrasing, or ask Durmah — it can answer from the
                manual directly.
              </p>
              <Link
                href="/support"
                prefetch={false}
                className="inline-flex items-center gap-1.5 text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:underline"
              >
                Contact support <ArrowRight className="w-3 h-3" aria-hidden="true" />
              </Link>
            </div>
          ) : (
            results.map((hit, index) => {
              const category = getManualCategory(hit.section.category);
              const gated = hit.section.audience === "member" && !isSignedIn;
              return (
                <Link
                  key={hit.section.slug}
                  href={`/manual/${hit.section.slug}`}
                  prefetch={false}
                  ref={(el) => {
                    resultRefs.current[index] = el;
                  }}
                  role="option"
                  aria-selected={activeIndex === index}
                  onMouseEnter={() => setActiveIndex(index)}
                  className={`block px-5 py-4 border-b border-gray-100 dark:border-slate-800/70 last:border-0 transition-colors ${
                    activeIndex === index
                      ? "bg-indigo-50 dark:bg-indigo-950/30"
                      : "hover:bg-gray-50 dark:hover:bg-slate-900/60"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-black uppercase tracking-widest text-indigo-500">
                      {category?.label ?? "Manual"}
                    </span>
                    {gated ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-gray-400">
                        <Lock className="w-2.5 h-2.5" aria-hidden="true" />
                        Members
                      </span>
                    ) : null}
                  </div>
                  <p className="text-sm font-bold text-gray-900 dark:text-white mb-1">
                    {hit.section.title}
                  </p>
                  <p className="text-xs leading-relaxed text-gray-500 dark:text-slate-400 line-clamp-2">
                    {hit.excerpt}
                  </p>
                </Link>
              );
            })
          )}

          {results.length > 0 ? (
            <div className="px-5 py-2.5 bg-gray-50 dark:bg-slate-900/70 flex items-center justify-between text-[10px] text-gray-400">
              <span>
                {results.length} {results.length === 1 ? "result" : "results"}
              </span>
              <span className="hidden sm:flex items-center gap-1.5">
                <kbd className="px-1.5 py-0.5 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded shadow-sm">
                  ↑↓
                </kbd>
                to navigate
                <kbd className="px-1.5 py-0.5 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded shadow-sm">
                  Enter
                </kbd>
                to open
              </span>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
